import { type AnnotationsMap, makeAutoObservable } from 'mobx';

import { DeliveryMethods, type ETACalculation } from '~/api/ETA';
import { company } from '~/company/Company';

// Imported from the module rather than the barrel: the barrel also pulls in
// DeliveryStore.tsx, which imports `~/stores` — a cycle this store sits inside.
import { NoWarehouseError } from '../DeliveryStore/NoWarehouseError';
import type { OrderStore } from '../OrderStore/OrderStore';
import { Query } from '../shared/Query';
import type { UserStore } from '../UserStore';

const PROBE_STALE_TIME = 5 * 60 * 1000;

/**
 * Owns two ETA probe queries (courier + pickup) for the current delivery
 * address and derives per-method availability. Source of truth for the header
 * delivery switcher's visibility and the checkout delivery/pickup selector.
 * Store-owned (not React-mounted) so availability is live on every page.
 */
export class DeliveryMethodAvailabilityStore {
  private readonly _userStore: UserStore;
  private readonly _orderStore: OrderStore;
  private declare readonly _courierProbe: Query<ETACalculation | null>;
  private declare readonly _pickupProbe: Query<ETACalculation | null>;

  constructor(userStore: UserStore, orderStore: OrderStore) {
    this._userStore = userStore;
    this._orderStore = orderStore;

    // `_probeConfig` is excluded so MobX does not wrap it as an action; the
    // Query config functions must read observables tracked (not inside an
    // action) for the probes to refetch when the delivery address changes.
    // The cast works around `keyof this` not resolving private members here,
    // which otherwise fails the override object's excess-property check
    // (same workaround as the `_requestPromiseHolder` exclusion in
    // DeliveryStore.tsx).
    makeAutoObservable(this, { _probeConfig: false } as AnnotationsMap<
      DeliveryMethodAvailabilityStore,
      '_probeConfig'
    >);

    (
      this as unknown as { _courierProbe: Query<ETACalculation | null> }
    )._courierProbe = new Query<ETACalculation | null>(undefined, () =>
      this._probeConfig(DeliveryMethods.COURIER_DELIVERY),
    );
    (
      this as unknown as { _pickupProbe: Query<ETACalculation | null> }
    )._pickupProbe = new Query<ETACalculation | null>(undefined, () =>
      this._probeConfig(DeliveryMethods.CLICK_AND_COLLECT),
    );
  }

  private _probeConfig(method: DeliveryMethods) {
    const address = this._userStore.deliveryAddress;
    const pos = address
      ? `${address.coordinates.lat},${address.coordinates.lng}`
      : '';
    return {
      queryKey: ['eta-probe', method, pos] as const,
      enabled: company.showDeliveryMethodSwitcher && !!address,
      staleTime: PROBE_STALE_TIME,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        this._orderStore.probeETA(pos, method, { signal }).catch((error) => {
          // "No warehouse for this method here" is an answer — resolve it as
          // one. Anything else (the network, a 5xx) is not, and swallowing it
          // as `null` too would make a dropped request indistinguishable from
          // a method that is genuinely unavailable.
          if (error instanceof NoWarehouseError) {
            return null;
          }
          throw error;
        }),
    };
  }

  get courierAvailable(): boolean {
    return !!this._courierProbe.result.data?.warehouse;
  }

  get pickupAvailable(): boolean {
    return !!this._pickupProbe.result.data?.warehouse;
  }

  get courierEtaRange(): string | undefined {
    return this._courierProbe.result.data?.duration.range;
  }

  get pickupEtaRange(): string | undefined {
    return this._pickupProbe.result.data?.duration.range;
  }

  get courierWarehouseCode(): string | undefined {
    return this._courierProbe.result.data?.warehouse.code;
  }

  get pickupWarehouseCode(): string | undefined {
    return this._pickupProbe.result.data?.warehouse.code;
  }

  /**
   * True when courier and pickup resolve to two distinct warehouses. Switching
   * the method then means switching warehouse, which invalidates the cart built
   * against the previous one.
   */
  get warehousesDiffer(): boolean {
    const courier = this.courierWarehouseCode;
    const pickup = this.pickupWarehouseCode;

    return !!courier && !!pickup && courier !== pickup;
  }

  get availableMethods(): DeliveryMethods[] {
    return [
      DeliveryMethods.COURIER_DELIVERY,
      DeliveryMethods.CLICK_AND_COLLECT,
    ].filter((method) =>
      method === DeliveryMethods.COURIER_DELIVERY
        ? this.courierAvailable
        : this.pickupAvailable,
    );
  }

  get isSwitcherVisible(): boolean {
    return this.courierAvailable && this.pickupAvailable;
  }

  /**
   * True while either probe is still resolving for the current address. Until
   * both settle, an unresolved probe reads as "no warehouse", so availability
   * (and `availableMethods`) is only trustworthy once this is false. Consumers
   * that realign the selected method must wait for this to avoid switching on a
   * transient half-loaded state (e.g. pickup resolves before courier).
   */
  get isProbing(): boolean {
    const courier = this._courierProbe.result;
    const pickup = this._pickupProbe.result;

    return (
      courier.isPending ||
      courier.isFetching ||
      pickup.isPending ||
      pickup.isFetching
    );
  }

  /**
   * True when both probes have settled and neither found a warehouse — nothing
   * delivers to this address by any method.
   *
   * Requires both to have answered without erroring: a failed request resolves
   * to no data exactly like an unserved method does, and reading that as "the
   * address is unusable" would block the checkout over a dropped connection.
   * Only meaningful where the probes run at all, which is the switcher
   * tenants; elsewhere the unscoped ETA answers the same question directly.
   */
  get isAddressUnserved(): boolean {
    if (
      !company.showDeliveryMethodSwitcher ||
      !this._userStore.deliveryAddress
    ) {
      return false;
    }

    const courier = this._courierProbe.result;
    const pickup = this._pickupProbe.result;

    if (this.isProbing || courier.isError || pickup.isError) {
      return false;
    }

    return !this.courierAvailable && !this.pickupAvailable;
  }

  destroy(): void {
    this._courierProbe.destroy();
    this._pickupProbe.destroy();
  }
}
